import { useEffect, useState } from "react";
import { ArrowUpRight, ChevronDown, Clock, Mail, MapPin, MessageCircle, Phone, X } from "lucide-react";
import { cn } from "@/lib/utils";

const SOCIAL_LABEL = { instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook", youtube: "YouTube" };

/** Turn a handle into an address, so it can be tapped rather than copied. */
function socialUrl(kind, handle) {
  const clean = handle.trim().replace(/^@/, "");
  if (clean.startsWith("http")) return clean;
  return {
    instagram: `https://instagram.com/${clean}`,
    tiktok: `https://tiktok.com/@${clean}`,
    facebook: `https://facebook.com/${clean}`,
    youtube: `https://youtube.com/@${clean}`,
  }[kind];
}

/**
 * One footer column. On a phone it folds shut behind its heading so the page
 * doesn't end in a wall of text; from tablet width up it's always open.
 */
function Column({ title, children, className, startOpen = false }) {
  const [open, setOpen] = useState(startOpen);
  return (
    <section className={cn("min-w-0 border-b border-line sm:border-0", className)}>
      <h2 className="m-0">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="w-full min-h-[48px] sm:min-h-0 flex items-center justify-between text-left text-[13px] font-semibold text-ink sm:pointer-events-none sm:mb-2.5"
        >
          {title}
          <ChevronDown
            className={cn("w-4 h-4 text-muted2 transition-transform sm:hidden", open && "rotate-180")}
          />
        </button>
      </h2>
      <div className={cn(open ? "block" : "hidden", "sm:block pb-4 sm:pb-0")}>{children}</div>
    </section>
  );
}

/** A story or policy, read in full over the page. */
function Reader({ entry, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={entry.title}
        className="bg-white w-full sm:max-w-xl max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-display text-[22px] font-semibold text-ink leading-tight">
            {entry.title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-10 h-10 -mr-2 -mt-1 rounded-full flex items-center justify-center text-muted2 hover:bg-surface shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[14px] leading-relaxed text-ink/80 whitespace-pre-line">{entry.body}</p>
      </div>
    </div>
  );
}

const linkCls = "inline-flex items-center gap-2 text-muted2 hover:text-[color:var(--shop)] transition-colors";

/**
 * The bottom of the storefront. The server only sends sections that are
 * switched on and filled in, so anything here is meant to show.
 */
export default function ShopFooter({ shop }) {
  const [reading, setReading] = useState(null);
  const f = shop.footer;
  if (!f) return null;

  const year = new Date().getFullYear();
  const wa = (f.contact?.whatsapp || "").replace(/\D/g, "");
  const directions =
    f.visit?.map_url ||
    (f.visit?.address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(f.visit.address)}`
      : "");
  const hasColumns =
    f.about || f.visit || f.contact || f.delivery || f.policies.length > 0 || f.sections.length > 0;
  if (!hasColumns && !f.show_bottom) return null;

  return (
    <footer className="border-t border-line bg-white">
      <div className="max-w-[1120px] mx-auto px-5 sm:px-8 pt-10 pb-7">
        {hasColumns && (
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:gap-x-12 sm:gap-y-9">
            {f.about && (
              <section className="sm:flex-[1.6_1_260px] min-w-0 pb-5 sm:pb-0 border-b border-line sm:border-0">
                <div
                  className="font-display text-[22px] font-semibold mb-2"
                  style={{ color: "var(--shop)" }}
                >
                  {shop.name}
                </div>
                {f.about.text && (
                  <p className="text-[13.5px] text-muted2 leading-relaxed whitespace-pre-line">
                    {f.about.text}
                  </p>
                )}
                {f.about.story && (
                  <button
                    type="button"
                    onClick={() => setReading({ title: "Our story", body: f.about.story })}
                    className="mt-2.5 text-[13px] font-semibold underline underline-offset-4"
                    style={{ color: "var(--shop)" }}
                  >
                    Read our story
                  </button>
                )}
              </section>
            )}

            {f.visit && (
              <Column title="Visit us" className="sm:flex-[1_1_180px]">
                <div className="space-y-3 text-[13px] text-muted2">
                  {f.visit.address && (
                    <p className="flex gap-2 leading-relaxed whitespace-pre-line">
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                      {f.visit.address}
                    </p>
                  )}
                  {f.visit.hours.length > 0 && (
                    <div className="flex gap-2">
                      <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                        {f.visit.hours.map((h, i) => (
                          <div key={i} className="contents">
                            <dt>{h.label}</dt>
                            <dd className="text-ink/80">{h.time}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                  {directions && (
                    <a
                      href={directions}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-semibold"
                      style={{ color: "var(--shop)" }}
                    >
                      Get directions <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </Column>
            )}

            {f.contact && (
              <Column title="Contact" className="sm:flex-[1_1_180px]">
                <div className="flex flex-col gap-2 text-[13px]">
                  {wa && (
                    <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className={linkCls}>
                      <MessageCircle className="w-4 h-4 shrink-0" /> WhatsApp {f.contact.whatsapp}
                    </a>
                  )}
                  {f.contact.phone && (
                    <a href={`tel:${f.contact.phone.replace(/[^\d+]/g, "")}`} className={linkCls}>
                      <Phone className="w-4 h-4 shrink-0" /> {f.contact.phone}
                    </a>
                  )}
                  {f.contact.email && (
                    <a href={`mailto:${f.contact.email}`} className={cn(linkCls, "break-all")}>
                      <Mail className="w-4 h-4 shrink-0" /> {f.contact.email}
                    </a>
                  )}
                  {(f.contact.socials.length > 0 || f.contact.links.length > 0) && (
                    <div className="flex flex-wrap gap-1.5 pt-1.5">
                      {f.contact.socials.map((s) => (
                        <a
                          key={s.kind}
                          href={socialUrl(s.kind, s.handle)}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full border border-line px-3 py-1.5 text-[12px] text-muted2 hover:border-[color:var(--shop)] hover:text-[color:var(--shop)] transition-colors"
                        >
                          {SOCIAL_LABEL[s.kind]}
                        </a>
                      ))}
                      {f.contact.links.map((l) => (
                        <a
                          key={l.url}
                          href={l.url}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full border border-line px-3 py-1.5 text-[12px] text-muted2 hover:border-[color:var(--shop)] hover:text-[color:var(--shop)] transition-colors"
                        >
                          {l.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </Column>
            )}

            {f.delivery && (
              <Column title="Delivery and payment" className="sm:flex-[1.2_1_200px]">
                {f.delivery.text && (
                  <p className="text-[13px] text-muted2 leading-relaxed whitespace-pre-line">
                    {f.delivery.text}
                  </p>
                )}
                {f.delivery.payments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {f.delivery.payments.map((p) => (
                      <span
                        key={p}
                        className="rounded-full px-2.5 py-1 text-[12px] font-semibold"
                        style={{
                          background: "color-mix(in srgb, var(--shop) 10%, white)",
                          color: "color-mix(in srgb, var(--shop) 85%, black)",
                        }}
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                )}
              </Column>
            )}

            {f.sections.map((s, i) => (
              <Column key={i} title={s.title} className="sm:flex-[1_1_200px]">
                <p className="text-[13px] text-muted2 leading-relaxed whitespace-pre-line">{s.body}</p>
              </Column>
            ))}

            {f.policies.length > 0 && (
              <Column title="Policies" className="sm:flex-[1_1_170px]">
                <ul className="flex flex-col gap-2 text-[13px]">
                  {f.policies.map((p, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => setReading(p)}
                        className="text-left text-muted2 hover:text-[color:var(--shop)] underline-offset-4 hover:underline"
                      >
                        {p.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </Column>
            )}
          </div>
        )}

        {f.show_bottom && (
          <div
            className={cn(
              "text-[12px] text-muted2 flex flex-wrap justify-between gap-2",
              hasColumns && "mt-8 pt-5 border-t border-line"
            )}
          >
            <span>{f.copyright || `© ${year} ${shop.name}`}</span>
          </div>
        )}
      </div>

      {reading && <Reader entry={reading} onClose={() => setReading(null)} />}
    </footer>
  );
}
